import axios from 'axios';

const API_ASSEMP_URL = `${process.env.REACT_APP_ASSEMP_URL}/juridico-ms/api`;

const processosService = {
    /**
     * Buscar processos por filtros
     */
    findProcessos: async (queryString: string) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/find?${queryString}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar processos:', error);
            throw error;
        }
    },

    /**
     * Buscar processo por ID
     */
    getProcessoById: async (id: number) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar processo por ID:', error);
            throw error;
        }
    },

    /**
     * Buscar advogados por filtros
     * @param filters - Objeto com filtros: { area, tipo, ativo }
     * @param paginacao - Objeto de paginação: { page, size, sort[] }
     */
    findAdvogados: async (filters?: { area?: string; tipo?: string; ativo?: boolean }, paginacao?: { page?: number; size?: number; sort?: string[] }) => {
        try {
            const params: any = {};
            
            // Adicionar filtros
            if (filters?.area) params.area = filters.area;
            if (filters?.tipo) params.tipo = filters.tipo;
            if (filters?.ativo !== undefined) params.ativo = filters.ativo;
            
            // Adicionar paginação (obrigatória pela API)
            // A API espera paginacao como objeto JSON stringificado no query string
            const paginacaoObj = paginacao || { page: 0, size: 1000, sort: [] };
            params.paginacao = JSON.stringify(paginacaoObj);
            
            const queryString = new URLSearchParams();
            Object.keys(params).forEach(key => {
                queryString.append(key, params[key]);
            });
            
            const response = await axios.get(`${API_ASSEMP_URL}/advogado/find?${queryString.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar advogados:', error);
            throw error;
        }
    },

    /**
     * Buscar associados por filtros
     */
    findAssociados: async (queryString?: string) => {
        try {
            const url = queryString 
                ? `${API_ASSEMP_URL}/associado/find?${queryString}`
                : `${API_ASSEMP_URL}/associado/find`;
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar associados:', error);
            throw error;
        }
    },

    /**
     * Criar novo processo
     */
    createProcesso: async (processoData: any) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/processo`, processoData);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar processo:', error);
            throw error;
        }
    },

    /**
     * Atualizar processo (using PATCH)
     */
    updateProcesso: async (processoData: any) => {
        try {
            const response = await axios.patch(`${API_ASSEMP_URL}/processo`, processoData);
            return response.data;
        } catch (error) {
            console.error('Erro ao atualizar processo:', error);
            throw error;
        }
    },

    /**
     * Delete processo by ID
     */
    deleteProcesso: async (id: number) => {
        try {
            const response = await axios.delete(`${API_ASSEMP_URL}/processo/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao deletar processo:', error);
            throw error;
        }
    },

    /**
     * Buscar observações de um processo por ID
     */
    findObservacoes: async (idProcesso: number) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/observacao/find/${idProcesso}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar observações do processo:', error);
            throw error;
        }
    },

    /**
     * Buscar observação por ID
     */
    getObservacaoById: async (id: number) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/observacao/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar observação por ID:', error);
            throw error;
        }
    },

    /**
     * Adicionar nova observação ao processo
     */
    createObservacao: async (observacaoData: { idProcesso: number; descricao: string }) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/processo/observacao`, observacaoData);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar observação:', error);
            throw error;
        }
    },

    /**
     * Remover observação do processo
     */
    deleteObservacao: async (id: number) => {
        try {
            const response = await axios.delete(`${API_ASSEMP_URL}/processo/observacao/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao deletar observação:', error);
            throw error;
        }
    },

    /**
     * Buscar tipos de ação ativos
     */
    findTiposAcaoAtivos: async () => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/tipoAcao/activated`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar tipos de ação:', error);
            throw error;
        }
    },

    /**
     * Buscar andamentos de um processo por ID
     */
    findAndamentos: async (idProcesso: number) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/andamento/find/${idProcesso}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar andamentos do processo:', error);
            throw error;
        }
    },

    /**
     * Buscar andamento por ID
     */
    getAndamentoById: async (id: number) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/andamento/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar andamento por ID:', error);
            throw error;
        }
    },

    /**
     * Adicionar novo andamento ao processo
     */
    createAndamento: async (andamentoData: { idProcesso: number; descricao: string; data: string | null; lancarPrazo?: boolean }) => {
        try {
            const payload = {
                ...andamentoData,
                lancarPrazo: andamentoData.lancarPrazo ?? false,
            };
            const response = await axios.post(`${API_ASSEMP_URL}/processo/andamento`, payload);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar andamento:', error);
            throw error;
        }
    },

    /**
     * Remover andamento do processo
     */
    deleteAndamento: async (id: number) => {
        try {
            const response = await axios.delete(`${API_ASSEMP_URL}/processo/andamento/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao deletar andamento:', error);
            throw error;
        }
    },

    /**
     * Buscar audiências de um processo por número do processo
     */
    findAudiencias: async (numeroProcesso: string) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/audiencia/find`, {
                params: {
                    numeroProcesso: numeroProcesso
                }
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar audiências do processo:', error);
            throw error;
        }
    },

    /**
     * Buscar audiências de um processo por ID do processo
     */
    findAudienciasByIdProcesso: async (idProcesso: number, paginacao?: { page?: number; size?: number; sort?: string[] }) => {
        try {
            // const paginacaoObj = paginacao || { page: 0, size: 1000, sort: [] };
            const response = await axios.get(`${API_ASSEMP_URL}/processo/audiencia/${idProcesso}/find`, {
                // params: {
                //     paginacao
                // }
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar audiências do processo por ID:', error);
            throw error;
        }
    },

    /**
     * Buscar audiências com filtros
     */
    findAudienciasByFilter: async (params: any) => {
        try {
            // Remover valores vazios/null/undefined dos params, mas manter todos os valores válidos
            const cleanParams: any = {};
            Object.keys(params).forEach(key => {
                const value = params[key];
                
                // Verificar se o valor é válido (não é null, undefined ou string vazia)
                const isValid = value !== null && 
                               value !== undefined && 
                               value !== '' &&
                               !(Array.isArray(value) && value.length === 0) &&
                               !(typeof value === 'object' && Object.keys(value).length === 0);
                
                if (isValid) {
                    // Se for paginacao, já deve vir como JSON string
                    if (key === 'paginacao' && typeof value === 'string') {
                        cleanParams[key] = value;
                    } else if (key !== 'paginacao') {
                        cleanParams[key] = value;
                    }
                }
            });
            
            console.log('🔍 Parâmetros limpos enviados para API:', cleanParams);
            
            const response = await axios.get(`${API_ASSEMP_URL}/processo/audiencia/find`, {
                params: cleanParams
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar audiências com filtros:', error);
            throw error;
        }
    },

    /**
     * Criar nova audiência
     */
    createAudiencia: async (audienciaData: any) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/processo/audiencia`, audienciaData);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar audiência:', error);
            throw error;
        }
    },

    /**
     * Buscar audiência por ID
     */
    getAudienciaById: async (id: number) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/audiencia/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar audiência por ID:', error);
            throw error;
        }
    },

    /**
     * Atualizar audiência
     */
    updateAudiencia: async (audienciaData: any) => {
        try {
            const response = await axios.put(`${API_ASSEMP_URL}/processo/audiencia`, audienciaData);
            return response.data;
        } catch (error) {
            console.error('Erro ao atualizar audiência:', error);
            throw error;
        }
    },

    /**
     * Deletar audiência por ID
     */
    deleteAudiencia: async (id: number) => {
        try {
            const response = await axios.delete(`${API_ASSEMP_URL}/processo/audiencia/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao deletar audiência:', error);
            throw error;
        }
    },

    /**
     * Buscar instâncias ativas
     */
    findInstanciasAtivas: async () => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/instancia/activated`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar instâncias:', error);
            throw error;
        }
    },

    // ========== ACTION TYPE ==========
    /**
     * Find all action types (with pagination)
     */
    findTiposAcao: async (pageable?: { page?: number; size?: number; sort?: string[] }) => {
        try {
            const params: any = {};
            if (pageable) {
                params.page = pageable.page || 0;
                params.size = pageable.size || 10;
                if (pageable.sort && pageable.sort.length > 0) {
                    // Axios handles array params by repeating the key
                    params.sort = pageable.sort;
                }
            }
            const response = await axios.get(`${API_ASSEMP_URL}/processo/tipoAcao`, { 
                params,
                paramsSerializer: {
                    indexes: null // This allows arrays to be serialized as sort=value1&sort=value2
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching action types:', error);
            throw error;
        }
    },

    /**
     * Create new action type
     */
    createTipoAcao: async (tipoAcaoData: { descricao: string; ativo: boolean }) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/processo/tipoAcao`, tipoAcaoData);
            return response.data;
        } catch (error) {
            console.error('Error creating action type:', error);
            throw error;
        }
    },

    /**
     * Enable action type
     */
    enableTipoAcao: async (id: number) => {
        try {
            const response = await axios.put(`${API_ASSEMP_URL}/processo/tipoAcao/${id}/enable`, {});
            return response.data;
        } catch (error) {
            console.error('Error enabling action type:', error);
            throw error;
        }
    },

    /**
     * Disable action type
     */
    disableTipoAcao: async (id: number) => {
        try {
            const response = await axios.put(`${API_ASSEMP_URL}/processo/tipoAcao/${id}/disable`, {});
            return response.data;
        } catch (error) {
            console.error('Error disabling action type:', error);
            throw error;
        }
    },

    // ========== INSTANCE ==========
    /**
     * Find all instances
     * Returns an array of strings: ["PRIMEIRA_INSTANCIA", "SEGUNDA_INSTANCIA", ...]
     */
    findInstancias: async () => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/instancia`);
            // API returns an array of strings directly
            return response.data;
        } catch (error) {
            console.error('Error fetching instances:', error);
            throw error;
        }
    },

    /**
     * Create new instance
     */
    createInstancia: async (instanciaData: { descricao: string; ativo: boolean }) => {
        try {
            const response = await axios.post(`${API_ASSEMP_URL}/processo/instancia`, instanciaData);
            return response.data;
        } catch (error) {
            console.error('Error creating instance:', error);
            throw error;
        }
    },

    /**
     * Enable instance
     */
    enableInstancia: async (id: number) => {
        try {
            const response = await axios.put(`${API_ASSEMP_URL}/processo/instancia/${id}/enable`, {});
            return response.data;
        } catch (error) {
            console.error('Error enabling instance:', error);
            throw error;
        }
    },

    /**
     * Disable instance
     */
    disableInstancia: async (id: number) => {
        try {
            const response = await axios.put(`${API_ASSEMP_URL}/processo/instancia/${id}/disable`, {});
            return response.data;
        } catch (error) {
            console.error('Error disabling instance:', error);
            throw error;
        }
    },

    /**
     * Buscar prazos de um processo por ID
     */
    findPrazos: async (idProcesso: number) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/prazo/find/${idProcesso}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar prazos do processo:', error);
            throw error;
        }
    },

    /**
     * Buscar precatórios de um processo por ID
     */
    findPrecatorios: async (idProcesso: number) => {
        try {
            const response = await axios.get(`${API_ASSEMP_URL}/processo/precatorio/find/${idProcesso}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar precatórios do processo:', error);
            throw error;
        }
    },

    /**
     * Find deadlines by filters
     */
    findPrazosByFilter: async (params?: any) => {
        try {
            const cleanParams: any = {};
            if (params) {
                Object.keys(params).forEach(key => {
                    if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
                        cleanParams[key] = params[key];
                    }
                });
            }
            
            const response = await axios.get(`${API_ASSEMP_URL}/processo/prazo/find`, {
                params: cleanParams
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar prazos com filtros:', error);
            throw error;
        }
    },

    /**
     * Get legal dashboard statistics
     * Filters by today's date and logged-in user
     */
    getDashboardStats: async (userId: number) => {
        try {
            const hoje = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

            // Find lawyer for logged-in user
            const advogadosResponse = await processosService.findAdvogados({ ativo: true });
            const advogadosList = Array.isArray(advogadosResponse)
                ? advogadosResponse
                : (advogadosResponse?.content || []);

            const advogadoDoUsuario = advogadosList.find((adv: any) => adv.idUsuario === userId);

            if (!advogadoDoUsuario) {
                // If lawyer not found, return zeros
                return {
                    prazosFatais: 0,
                    prazosIdeais: 0,
                    correcaoPrazoIdeal: 0,
                    correcaoPrazoFatal: 0,
                    iniciaisPendentes: 0,
                    calculos: 0,
                    audiencias: 0
                };
            }

            // Find fatal deadlines: dataPrazoFinal = today, for logged-in user
            const prazosFataisResponse = await processosService.findPrazosByFilter({
                dataPrazoFinal: hoje,
                advogado: advogadoDoUsuario.id
            });
            const prazosFataisCount = prazosFataisResponse?.totalElements !== undefined
                ? prazosFataisResponse.totalElements
                : (Array.isArray(prazosFataisResponse) ? prazosFataisResponse.length : (prazosFataisResponse?.content?.length || 0));

            // Find ideal deadlines: dataPrazoIdeal = today, for logged-in user
            const prazosIdeaisResponse = await processosService.findPrazosByFilter({
                dataPrazoIdeal: hoje,
                advogado: advogadoDoUsuario.id
            });
            const prazosIdeaisCount = prazosIdeaisResponse?.totalElements !== undefined
                ? prazosIdeaisResponse.totalElements
                : (Array.isArray(prazosIdeaisResponse) ? prazosIdeaisResponse.length : (prazosIdeaisResponse?.content?.length || 0));

            // Find hearings: dataInicial = today, for logged-in user
            const audienciasResponse = await processosService.findAudienciasByFilter({
                dataInicial: hoje,
                advogado: advogadoDoUsuario.id
            });
            const audienciasCount = audienciasResponse?.totalElements !== undefined
                ? audienciasResponse.totalElements
                : (Array.isArray(audienciasResponse) ? audienciasResponse.length : (audienciasResponse?.content?.length || 0));

            // Find iniciais pendentes for logged-in user
            let iniciaisPendentesCount = 0;
            try {
                const iniciaisResponse = await processosService.findIniciaisByFilter({
                    advogado: advogadoDoUsuario.id
                });
                iniciaisPendentesCount = iniciaisResponse?.totalElements !== undefined
                    ? iniciaisResponse.totalElements
                    : (Array.isArray(iniciaisResponse) ? iniciaisResponse.length : (iniciaisResponse?.content?.length || 0));
            } catch (e) {
                console.warn('Could not fetch iniciais pendentes');
            }

            // Find calculos for logged-in user
            let calculosCount = 0;
            try {
                const calculosResponse = await processosService.findCalculosByFilter({
                    advogado: advogadoDoUsuario.id
                });
                calculosCount = calculosResponse?.totalElements !== undefined
                    ? calculosResponse.totalElements
                    : (Array.isArray(calculosResponse) ? calculosResponse.length : (calculosResponse?.content?.length || 0));
            } catch (e) {
                console.warn('Could not fetch calculos');
            }

            return {
                prazosFatais: prazosFataisCount,
                prazosIdeais: prazosIdeaisCount,
                correcaoPrazoIdeal: 0, // No endpoint available
                correcaoPrazoFatal: 0, // No endpoint available
                iniciaisPendentes: iniciaisPendentesCount,
                calculos: calculosCount,
                audiencias: audienciasCount
            };
        } catch (error) {
            console.error('Erro ao buscar estatísticas do dashboard:', error);
            // Return default values in case of error
            return {
                prazosFatais: 0,
                prazosIdeais: 0,
                correcaoPrazoIdeal: 0,
                correcaoPrazoFatal: 0,
                iniciaisPendentes: 0,
                calculos: 0,
                audiencias: 0
            };
        }
    },

    /**
     * Update process status
     */
    updateStatus: async (id: number, statusProcesso: string) => {
        try {
            const response = await axios.put(`${API_ASSEMP_URL}/processo/update/status`, {
                id,
                statusProcesso
            });
            return response.data;
        } catch (error) {
            console.error('Error updating process status:', error);
            throw error;
        }
    },

    /**
     * Find iniciais pendentes by filter
     */
    findIniciaisByFilter: async (params?: any) => {
        try {
            const cleanParams: any = {};
            if (params) {
                Object.keys(params).forEach(key => {
                    if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
                        cleanParams[key] = params[key];
                    }
                });
            }
            
            const response = await axios.get(`${API_ASSEMP_URL}/inicial/find`, {
                params: cleanParams
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching iniciais:', error);
            throw error;
        }
    },

    /**
     * Find calculos by filter
     */
    findCalculosByFilter: async (params?: any) => {
        try {
            const cleanParams: any = {};
            if (params) {
                Object.keys(params).forEach(key => {
                    if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
                        cleanParams[key] = params[key];
                    }
                });
            }
            
            const response = await axios.get(`${API_ASSEMP_URL}/calculo/find`, {
                params: cleanParams
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching calculos:', error);
            throw error;
        }
    },
};

export default processosService;
